import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  CheckCircle2,
  DoorOpen,
  Filter,
  Maximize2,
  MonitorUp,
  Search,
  ShieldAlert,
  SlidersHorizontal,
  Trash2,
  UserRound,
  UserPlus,
  UserRoundCheck,
  X,
} from 'lucide-react';

import api, { getApiErrorMessage } from '../../services/api';

import {
  clearNewEventHighlights,
  fetchMonitorData,
  mergeMonitorEvents,
} from '../../services/monitorService';

const EMPTY_FILTERS = {
  search: '',
  group: '',
  device: '',
  userType: '',
  result: '',
  startDate: '',
  endDate: '',
};

const EMPTY_STATS = {
  entriesToday: 0,
  deniedToday: 0,
  visitorsToday: 0,
  onlineDevices: 0,
};

const EMPTY_OPTIONS = {
  groups: [],
  devices: [],
  userTypes: [],
  results: [],
};

const SAO_PAULO_TIMEZONE = 'America/Sao_Paulo';
const MONITOR_REFRESH_MS = 5000;
const FILTER_DEBOUNCE_MS = 400;
const NEW_EVENT_HIGHLIGHT_MS = 4200;

const REGISTER_ACTIONS = [
  { type: 'morador', label: 'Morador' },
  { type: 'visitante', label: 'Visitante' },
  { type: 'prestador', label: 'Prestador' },
];

export default function MonitorPage({ auth }) {
  const canDeleteLogs = auth?.user?.perfil === 'super_admin';
  const [deletingId, setDeletingId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const mutationVersionRef = useRef(0);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [filterOptions, setFilterOptions] = useState(EMPTY_OPTIONS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [registrationRequest, setRegistrationRequest] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const inFlightRef = useRef(false);
  const filtersStateRef = useRef(filters);
  const firstLoadRef = useRef(true);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setAppliedFilters(filters);
    }, FILTER_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [filters]);

  useEffect(() => {
    filtersStateRef.current = appliedFilters;
  }, [appliedFilters]);

  const refreshMonitor = useCallback(async ({ replace = false } = {}) => {
    if (inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;
    const mutationVersion = mutationVersionRef.current;
    const isFirstLoad = firstLoadRef.current || replace;
    if (isFirstLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    setError('');

    try {
      const data = await fetchMonitorData(filtersStateRef.current);
      if (mutationVersion !== mutationVersionRef.current) return;
      setStats((current) => (areStatsEqual(current, data.stats || EMPTY_STATS) ? current : data.stats || EMPTY_STATS));
      setFilterOptions(data.filterOptions || EMPTY_OPTIONS);

      setEvents((currentEvents) => {
        if (replace || firstLoadRef.current) {
          return data.events || [];
        }

        const merged = mergeMonitorEvents(currentEvents, data.events || []);
        if (merged.newKeys.length > 0) {
          window.setTimeout(() => {
            setEvents((latestEvents) => clearNewEventHighlights(latestEvents, merged.newKeys));
          }, NEW_EVENT_HIGHLIGHT_MS);
        }
        return merged.changed ? merged.events : currentEvents;
      });
      firstLoadRef.current = false;
    } catch (loadError) {
      console.error('Erro ao carregar Monitor:', loadError);
      setError('Não foi possível carregar os eventos de acesso.');
      if (isFirstLoad) {
        setEvents([]);
        setStats(EMPTY_STATS);
        setFilterOptions(EMPTY_OPTIONS);
      }
    } finally {
      inFlightRef.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    firstLoadRef.current = true;
    refreshMonitor({ replace: true });

    const intervalId = window.setInterval(() => {
      refreshMonitor();
    }, MONITOR_REFRESH_MS);

    return () => window.clearInterval(intervalId);
  }, [appliedFilters, refreshMonitor]);

  const visibleEvents = useMemo(() => {
    return events.filter((event) => {
      if (filters.group && event.group !== filters.group) return false;
      if (filters.userType && event.userType !== filters.userType) return false;
      return true;
    });
  }, [events, filters.group, filters.userType]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleRegisterUser = (event, userType) => {
    setRegistrationRequest({ event, userType });
  };

  const handleDeleteLog = async (event) => {
    if (!canDeleteLogs || deletingId !== null) return;
    if (!window.confirm(`Excluir o log de ${event.userName}, em ${formatDateTime(event.occurredAt)}? A foto capturada deste log também será removida. Esta ação não pode ser desfeita.`)) return;
    setDeletingId(event.id); setFeedback(null);
    try {
      await api.delete(`/controle-acesso/registros/${event.id}`);
      mutationVersionRef.current += 1;
      setEvents((current) => current.filter((item) => item.id !== event.id));
      setFeedback({ type: 'success', message: 'Log excluído com sucesso.' });
      await refreshMonitor();
    } catch (deleteError) {
      setFeedback({ type: 'error', message: getApiErrorMessage(deleteError, 'Não foi possível excluir o log.') });
    } finally { setDeletingId(null); }
  };

  const handleDeleteAllLogs = async () => {
    if (!canDeleteLogs || deletingId !== null) return;
    if (!window.confirm('Apagar TODOS os logs de acesso e suas fotos capturadas, de todos os dispositivos e períodos? Os filtros selecionados não limitam esta exclusão. Esta ação não pode ser desfeita.')) return;
    setDeletingId('todos'); setFeedback(null);
    try {
      const { data } = await api.delete('/controle-acesso/registros');
      mutationVersionRef.current += 1;
      setEvents([]); setStats(EMPTY_STATS);
      setFeedback({ type: 'success', message: `Todos os logs foram apagados. ${data?.total_removido ?? 0} registro(s) removido(s).` });
      await refreshMonitor();
    } catch (deleteError) {
      setFeedback({ type: 'error', message: getApiErrorMessage(deleteError, 'Não foi possível apagar todos os logs.') });
    } finally { setDeletingId(null); }
  };

  const handleRegistrationSaved = () => {
    setRegistrationRequest(null);
    refreshMonitor({ replace: true });
  };

  const handleFullscreen = () => {
    const root = document.documentElement;
    if (!document.fullscreenElement && root.requestFullscreen) {
      root.requestFullscreen();
      return;
    }
    if (document.exitFullscreen) {
      document.exitFullscreen();
    }
  };

  return (
    <div className="min-h-full p-0 text-slate-700">
      <MonitorHeader
        refreshing={refreshing}
        connectionState={error ? 'error' : loading ? 'loading' : 'online'}
        onFiltersClick={() => setFiltersOpen((current) => !current)}
        filtersOpen={filtersOpen}
        onFullscreen={handleFullscreen}
      />

      <div className="mt-4 space-y-3">
        <MonitorStats stats={stats} loading={loading} />
        {filtersOpen && <MonitorFilters filters={filters} options={filterOptions} onChange={handleFilterChange} onClear={() => setFilters(EMPTY_FILTERS)} onDeleteAll={canDeleteLogs ? handleDeleteAllLogs : undefined} deletingId={deletingId} />}
        {feedback && <div role="status" className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm ${feedback.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}><span>{feedback.message}</span><button type="button" aria-label="Fechar mensagem" onClick={() => setFeedback(null)}><X size={17} /></button></div>}
        <div className="flex items-center justify-between px-1 text-xs text-slate-500"><span className="font-semibold">{visibleEvents.length} registro(s) exibido(s)</span><span>Atualização automática a cada 5 segundos</span></div>
        <MonitorEventList events={visibleEvents} loading={loading} error={error} onRegisterUser={handleRegisterUser} onDeleteLog={canDeleteLogs ? handleDeleteLog : undefined} deletingId={deletingId} />
      </div>

      {registrationRequest ? (
        <MonitorRegistrationModal
          request={registrationRequest}
          onClose={() => setRegistrationRequest(null)}
          onSaved={handleRegistrationSaved}
        />
      ) : null}
    </div>
  );
}

function MonitorHeader({ refreshing, onFiltersClick, onFullscreen, filtersOpen, connectionState }) {
  return (
    <header className="flex flex-col gap-2 border-b border-slate-200 pb-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-black tracking-tight text-slate-800 sm:text-2xl">Entrada e Saída</h1>
        <span className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-xs font-black shadow-sm ${connectionState === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
          <span className={`h-2.5 w-2.5 rounded-full ${connectionState === 'error' ? 'bg-red-500' : 'bg-emerald-400'} ${refreshing ? 'animate-pulse' : ''}`} />
          {connectionState === 'error' ? 'Sem conexão' : connectionState === 'loading' ? 'Conectando...' : refreshing ? 'Atualizando' : 'Online'}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={onFiltersClick} aria-expanded={filtersOpen} aria-controls="monitor-filters" className="monitor-toolbar-button">
          <SlidersHorizontal size={17} />
          Filtros
        </button>
        <button type="button" onClick={onFullscreen} aria-label="Alternar tela cheia" title="Tela cheia" className="monitor-icon-button">
          <Maximize2 size={18} />
        </button>
      </div>
    </header>
  );
}

function MonitorStats({ stats, loading }) {
  const cards = [
    { title: 'Entradas', value: stats.entriesToday, icon: <DoorOpen size={22} />, tone: 'emerald' },
    { title: 'Negadas', value: stats.deniedToday, icon: <ShieldAlert size={22} />, tone: 'red' },
    { title: 'Visitantes', value: stats.visitorsToday, icon: <UserRoundCheck size={22} />, tone: 'blue' },
    { title: 'Dispositivos', value: stats.onlineDevices, icon: <MonitorUp size={22} />, tone: 'purple' },
  ];

  return (
    <section className="grid grid-cols-2 gap-2 xl:grid-cols-4">
      {cards.map((card) => (
        <article key={card.title} className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm">
          <div className="flex items-center gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${getToneClasses(card.tone).icon}`}>
              {card.icon}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase text-slate-500">{card.title}</p>
              <p className="mt-0.5 text-2xl font-black leading-none text-slate-800">{loading ? '—' : card.value}</p>
            </div>
          </div>
        </article>
      ))}
    </section>
  );
}

function MonitorFilters({ filters, options, onChange, onClear, onDeleteAll, deletingId }) {
  return (
    <section id="monitor-filters" className="rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4 xl:items-end">
        <label className="monitor-field-label">
          Pesquisa
          <div className="relative mt-1.5">
            <Search className="pointer-events-none absolute left-3 top-3 text-slate-500" size={17} />
            <input
              type="text"
              placeholder="Nome"
              className="monitor-input pl-9"
              value={filters.search}
              onChange={(event) => onChange('search', event.target.value)}
            />
          </div>
        </label>
        <SelectFilter label="Grupo" value={filters.group} options={options.groups} onChange={(value) => onChange('group', value)} />
        <SelectFilter label="Dispositivo" value={filters.device} options={options.devices} onChange={(value) => onChange('device', value)} />
        <SelectFilter label="Tipo" value={filters.userType} options={options.userTypes} onChange={(value) => onChange('userType', value)} />
        <SelectFilter label="Resultado" value={filters.result} options={options.results} onChange={(value) => onChange('result', value)} />
        <div className="grid grid-cols-2 gap-2">
          <label className="monitor-field-label">
            Período inicial
            <input type="date" className="monitor-input mt-1.5" value={filters.startDate} onChange={(event) => onChange('startDate', event.target.value)} />
          </label>
          <label className="monitor-field-label">
            Período final
            <input type="date" className="monitor-input mt-1.5" value={filters.endDate} onChange={(event) => onChange('endDate', event.target.value)} />
          </label>
        </div>
        <button type="button" onClick={onClear} className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-black text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-condo-teal">
          <Filter size={16} />
          Limpar filtros
        </button>
        {onDeleteAll && <button type="button" onClick={onDeleteAll} disabled={deletingId !== null} className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 text-xs font-bold text-red-700 transition hover:bg-red-100 disabled:opacity-50"><Trash2 size={16} />{deletingId === 'todos' ? 'Apagando logs...' : 'Apagar todos os logs'}</button>}
      </div>
    </section>
  );
}

function SelectFilter({ label, value, options, onChange }) {
  return (
    <label className="monitor-field-label">
      {label}
      <select className="monitor-input mt-1.5" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Todos</option>
        {(options || []).map((option) => (
          <option key={option} value={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}

function MonitorEventList({ events, loading, error, onRegisterUser, onDeleteLog, deletingId }) {
  if (loading) {
    return <MonitorLoadingState />;
  }

  if (error) {
    return <MonitorEmptyState title={error} description="Tente novamente em instantes ou verifique a conexão com a API." />;
  }

  if (events.length === 0) {
    return (
      <MonitorEmptyState
        title="Nenhum evento de acesso recebido."
        description="Os novos eventos aparecerão aqui quando forem enviados pelos dispositivos integrados."
      />
    );
  }

  return (
    <section className="space-y-2">
      {events.map((event) => (
        <MonitorEventCard key={`${event.deviceId || 'device'}:${event.eventFaceId || event.id}`} event={event} onRegisterUser={onRegisterUser} onDeleteLog={onDeleteLog} deletingId={deletingId} />
      ))}
    </section>
  );
}

export function MonitorEventCard({ event, onRegisterUser, onDeleteLog, deletingId }) {
  const allowed = event.resultStatus === 'autorizado';

  return (
    <article style={{ borderLeftColor: allowed ? '#10b981' : '#ef4444' }} className={`rounded-2xl border border-l-4 px-4 py-4 shadow-sm transition-colors duration-500 ${event.isNew ? 'border-cyan-200 bg-cyan-50' : 'border-slate-200 bg-white hover:shadow-md'}`}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-[140px_152px_minmax(0,1fr)_180px_200px] xl:items-center xl:divide-x xl:divide-slate-200">
        <PhotoBlock label="Foto cadastrada" src={event.registeredPhotoUrl} alt={`Foto cadastrada de ${event.userName}`} emptyText="Sem foto cadastrada" />
        <PhotoBlock label="Foto capturada" src={event.capturedPhotoUrl} alt={`Foto capturada de ${event.userName}`} className="xl:pl-3" emptyText="Sem foto capturada" />

        <div className="min-w-0 xl:px-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="break-words text-lg font-black leading-tight text-slate-800">{event.userName}</h2>
            <TypeBadge type={event.userType} />
          </div>
          <div className="mt-2 grid grid-cols-1 gap-1.5 text-xs text-slate-600 sm:grid-cols-2">
            <InfoLine label="Unidade" value={formatUnit(event.apartment, event.tower)} />
            <InfoLine label="Método" value={event.authMethod} />
            <InfoLine label="Local" value={event.locationName} wide />
          </div>
        </div>

        <div className="space-y-1.5 text-xs text-slate-600 xl:px-3">
          <InfoLine icon={<CalendarDays size={14} />} label="Horário" value={formatDateTime(event.occurredAt)} />
          <InfoLine label="Dispositivo" value={event.deviceName} />
          <InfoLine label="Modelo" value={event.deviceModel} />
        </div>

        <div className="space-y-2 xl:pl-3">
          {event.userType === 'Desconhecido' && <div><p className="mb-1.5 text-[10px] font-semibold uppercase text-slate-500">Cadastrar como</p><div className="grid grid-cols-3 gap-1.5">
            {REGISTER_ACTIONS.map((action) => (
              <button
                key={action.type}
                type="button"
                onClick={() => onRegisterUser?.(event, action.type)}
                className="inline-flex min-h-9 items-center justify-center gap-1 rounded-lg border border-slate-200 bg-white px-1.5 text-[10px] font-black uppercase leading-tight text-slate-700 transition hover:border-condo-teal hover:bg-cyan-50 hover:text-condo-dark focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
              >
                <UserPlus size={13} />
                <span className="break-words">{action.label}</span>
              </button>
            ))}
          </div></div>}
          <div className={`flex min-h-[76px] items-center gap-3 rounded-xl border px-3 py-2.5 ${allowed ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${allowed ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200' : 'bg-red-100 text-red-700 ring-1 ring-red-200'}`}>
              {allowed ? <CheckCircle2 size={26} /> : <ShieldAlert size={26} />}
            </div>
            <div className="min-w-0 text-left"><p className={`break-words text-base font-black leading-tight ${allowed ? 'text-emerald-700' : 'text-red-700'}`}>{event.result}</p>
            {event.denialReason ? <p className="mt-1 break-words text-xs font-semibold leading-snug text-slate-600">{event.denialReason}</p> : null}</div>
          </div>
          {onDeleteLog && <button type="button" disabled={deletingId != null} onClick={() => onDeleteLog(event)} className="inline-flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50"><Trash2 size={15} />{deletingId === event.id ? 'Excluindo...' : 'Excluir log'}</button>}
        </div>
      </div>
    </article>
  );
}

function PhotoBlock({ label, src, alt, className = '', emptyText = 'Sem foto' }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [failedSrc, setFailedSrc] = useState(null);
  const shouldLoad = Boolean(src) && failedSrc !== src;
  const isLoading = shouldLoad && !imageSrc;

  useEffect(() => {
    if (!shouldLoad) {
      setImageSrc(null);
      return undefined;
    }

    const controller = new AbortController();
    let objectUrl = null;
    setImageSrc(null);

    async function loadImage() {
      try {
        const headers = {};
        const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await fetch(src, { headers, signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Imagem indisponivel: ${response.status}`);
        }

        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      } catch (error) {
        if (error.name !== 'AbortError') {
          setFailedSrc(src);
        }
      }
    }

    loadImage();

    return () => {
      controller.abort();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, shouldLoad]);

  return (
    <div className={`min-w-0 ${className}`}>
      <p className="mb-1.5 text-center text-[10px] font-black uppercase text-slate-500 xl:text-left">{label}</p>
      <div className="relative mx-auto flex aspect-square w-full max-w-[150px] items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 text-slate-400 shadow-inner sm:max-w-[140px] xl:mx-0 xl:h-[136px] xl:w-[136px] xl:max-w-none">
        {isLoading ? <div className="absolute inset-0 animate-pulse bg-slate-200" /> : null}
        {shouldLoad && imageSrc ? (
          <img
            src={imageSrc}
            alt={alt}
            className="h-full w-full object-cover transition-opacity duration-200"
            onError={() => setFailedSrc(src)}
          />
        ) : !isLoading ? (
          <div className="flex flex-col items-center gap-1.5 px-2 text-center text-[11px] font-bold leading-tight text-slate-500">
            <UserRound size={30} />
            {emptyText}
          </div>
        ) : null}
      </div>
    </div>
  );
}


function MonitorRegistrationModal({ request, onClose, onSaved }) {
  const { event, userType } = request;
  const [formData, setFormData] = useState(() => buildInitialRegistrationForm(event, userType));
  const [moradores, setMoradores] = useState([]);
  const [moradorSearch, setMoradorSearch] = useState('');
  const [useCapturedPhoto, setUseCapturedPhoto] = useState(Boolean(event.capturedPhotoUrl));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const needsResident = userType === 'visitante' || userType === 'prestador';
  const title = getRegistrationTitle(userType);

  useEffect(() => {
    if (!needsResident) {
      return;
    }

    let active = true;
    const endpoint = userType === 'prestador' ? '/contratados/moradores' : '/visitantes/moradores';
    api.get(endpoint)
      .then((response) => {
        if (active) {
          setMoradores(response.data || []);
        }
      })
      .catch(() => {
        if (active) {
          setMoradores([]);
        }
      });

    return () => {
      active = false;
    };
  }, [needsResident, userType]);

  const moradoresFiltrados = moradores.filter((morador) => {
    const term = moradorSearch.trim().toLowerCase();
    if (!term) return true;
    return `${morador.nome || ''} ${morador.unidade || ''}`.toLowerCase().includes(term);
  }).slice(0, 8);

  const selectedMorador = moradores.find((morador) => String(morador.id) === String(formData.morador_id));

  const handleChange = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();
    setSaving(true);
    setError('');

    try {
      if (userType === 'morador' && onlyDigits(formData.cpf).length !== 11) {
        setError('Informe um CPF com 11 dígitos para cadastrar o morador.');
        setSaving(false);
        return;
      }
      if (needsResident && !formData.morador_id) {
        setError('Selecione o morador responsável.');
        setSaving(false);
        return;
      }

      const payload = new FormData();
      if (userType === 'morador') {
        payload.append('nome', formData.nome.trim());
        payload.append('cpf', onlyDigits(formData.cpf));
        payload.append('unidade', formData.unidade.trim().toUpperCase());
        payload.append('contato', onlyDigits(formData.contato));
        payload.append('placa_carro', formData.placa_carro.trim().toUpperCase());
        payload.append('status', 'true');
      } else {
        payload.append('nome_completo', formData.nome_completo.trim());
        payload.append('documento', onlyDigits(formData.documento) || formData.documento.trim());
        payload.append('contato', onlyDigits(formData.contato));
        payload.append('morador_id', String(formData.morador_id || ''));
      }

      if (useCapturedPhoto && event.capturedPhotoUrl) {
        const capturedFile = await fetchCapturedPhotoAsFile(event.capturedPhotoUrl, `${userType}-${event.id || 'acesso'}.jpg`);
        if (capturedFile) {
          payload.append('foto', capturedFile, capturedFile.name);
        }
      }

      const endpoint = userType === 'morador'
        ? '/moradores/'
        : userType === 'visitante'
          ? '/visitantes/'
          : '/contratados/';
      await api.post(endpoint, payload);
      onSaved();
    } catch (submitError) {
      setError(getApiErrorMessage(submitError, 'Não foi possível cadastrar este usuário.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/35 p-4 backdrop-blur-sm">
      <form onSubmit={handleSubmit} className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white text-slate-700 shadow-2xl shadow-slate-900/20">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="text-lg font-black text-slate-800">{title}</h2>
            <p className="text-xs font-semibold text-slate-500">Origem: {event.userName} · {event.deviceName}</p>
          </div>
          <button type="button" onClick={onClose} className="monitor-icon-button" aria-label="Fechar cadastro">
            <X size={18} />
          </button>
        </div>

        <div className="grid gap-4 p-4 md:grid-cols-[160px_1fr]">
          <div className="space-y-2">
            <PhotoBlock label="Foto capturada" src={event.capturedPhotoUrl} alt={`Foto capturada de ${event.userName}`} emptyText="Sem foto capturada" />
            <label className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white p-2 text-xs font-bold text-slate-600">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 bg-white accent-cyan-500"
                checked={useCapturedPhoto}
                disabled={!event.capturedPhotoUrl}
                onChange={(changeEvent) => setUseCapturedPhoto(changeEvent.target.checked)}
              />
              Usar foto capturada no cadastro
            </label>
          </div>

          <div className="space-y-3">
            {error ? <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-bold text-red-700">{error}</div> : null}

            {userType === 'morador' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <MonitorFormField label="Nome" value={formData.nome} onChange={(value) => handleChange('nome', value)} required />
                <MonitorFormField label="CPF" value={formData.cpf} onChange={(value) => handleChange('cpf', onlyDigits(value))} required maxLength={11} />
                <MonitorFormField label="Unidade" value={formData.unidade} onChange={(value) => handleChange('unidade', value)} required />
                <MonitorFormField label="Contato" value={formData.contato} onChange={(value) => handleChange('contato', onlyDigits(value).slice(0, 11))} maxLength={11} />
                <MonitorFormField label="Placa" value={formData.placa_carro} onChange={(value) => handleChange('placa_carro', value.toUpperCase())} />
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <MonitorFormField label="Nome" value={formData.nome_completo} onChange={(value) => handleChange('nome_completo', value)} required />
                <MonitorFormField label="Documento" value={formData.documento} onChange={(value) => handleChange('documento', onlyDigits(value))} required />
                <MonitorFormField label="Contato" value={formData.contato} onChange={(value) => handleChange('contato', onlyDigits(value).slice(0, 11))} maxLength={11} />
                <label className="monitor-field-label sm:col-span-2">
                  Morador responsável
                  <input
                    type="text"
                    className="monitor-input mt-1.5"
                    placeholder="Buscar morador"
                    value={moradorSearch}
                    onChange={(inputEvent) => setMoradorSearch(inputEvent.target.value)}
                  />
                  {selectedMorador ? (
                    <p className="mt-1 text-xs font-bold text-condo-dark">Selecionado: {selectedMorador.nome} {selectedMorador.unidade ? `(${selectedMorador.unidade})` : ''}</p>
                  ) : null}
                  <div className="mt-2 max-h-36 overflow-auto rounded-lg border border-slate-200 bg-white">
                    {moradoresFiltrados.map((morador) => (
                      <button
                        key={morador.id}
                        type="button"
                        onClick={() => {
                          handleChange('morador_id', morador.id);
                          setMoradorSearch(`${morador.nome}${morador.unidade ? ` (${morador.unidade})` : ''}`);
                        }}
                        className={`block w-full px-3 py-2 text-left text-xs font-bold transition hover:bg-cyan-50 ${String(formData.morador_id) === String(morador.id) ? 'bg-cyan-50 text-condo-dark' : 'text-slate-600'}`}
                      >
                        {morador.nome} {morador.unidade ? `(${morador.unidade})` : ''}
                      </button>
                    ))}
                    {!moradoresFiltrados.length ? <div className="px-3 py-2 text-xs font-bold text-slate-500">Nenhum morador encontrado</div> : null}
                  </div>
                </label>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-black text-slate-700 hover:bg-slate-50">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="rounded-lg border border-condo-teal bg-condo-teal px-4 py-2 text-sm font-black text-condo-dark transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60">
            {saving ? 'Cadastrando...' : title}
          </button>
        </div>
      </form>
    </div>
  );
}

function MonitorFormField({ label, value, onChange, required = false, maxLength }) {
  return (
    <label className="monitor-field-label">
      {label}
      <input
        type="text"
        className="monitor-input mt-1.5"
        value={value}
        required={required}
        maxLength={maxLength}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

async function fetchCapturedPhotoAsFile(url, filename) {
  const headers = {};
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, { headers });
  if (!response.ok) {
    return null;
  }

  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type || 'image/jpeg' });
}

function buildInitialRegistrationForm(event, userType) {
  const cleanCode = onlyDigits(event.facialCode || '');
  const cleanUnit = event.apartment && event.apartment !== '-' ? String(event.apartment).trim() : '';
  if (userType === 'morador') {
    return {
      nome: event.userName === 'Desconhecido' ? '' : event.userName,
      cpf: cleanCode.length === 11 ? cleanCode : '',
      unidade: cleanUnit,
      contato: '',
      placa_carro: '',
    };
  }

  return {
    nome_completo: event.userName === 'Desconhecido' ? '' : event.userName,
    documento: cleanCode,
    contato: '',
    morador_id: '',
  };
}

function getRegistrationTitle(userType) {
  if (userType === 'morador') return 'Cadastrar morador';
  if (userType === 'visitante') return 'Cadastrar visitante';
  return 'Cadastrar prestador';
}

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}


function MonitorEmptyState({ title, description }) {
  return (
    <section className="flex min-h-[360px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
      <div className="max-w-md">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-400">
          <MonitorUp size={26} />
        </div>
        <h2 className="mt-5 text-xl font-black text-slate-800">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-500">{description}</p>
      </div>
    </section>
  );
}

function MonitorLoadingState() {
  return (
    <section className="space-y-2">
      <div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" />
      <div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" />
      <div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" />
    </section>
  );
}

function InfoLine({ label, value, icon, wide = false }) {
  return (
    <div className={`min-w-0 ${wide ? 'sm:col-span-2' : ''}`}>
      <p className="flex items-center gap-1.5 text-[10px] font-black uppercase text-slate-500">
        {icon}
        {label}
      </p>
      <p className="mt-0.5 break-words font-bold text-slate-700">{value || '-'}</p>
    </div>
  );
}

function TypeBadge({ type }) {
  const styles = {
    Morador: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    Visitante: 'border-sky-200 bg-sky-50 text-sky-700',
    Prestador: 'border-orange-200 bg-orange-50 text-orange-700',
    Desconhecido: 'border-red-200 bg-red-50 text-red-700',
  };

  return <span className={`max-w-full rounded-md border px-2 py-0.5 text-[10px] font-black uppercase leading-tight break-words ${styles[type] || styles.Desconhecido}`}>{type}</span>;
}

function getToneClasses(tone) {
  const styles = {
    emerald: { icon: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200' },
    red: { icon: 'bg-red-50 text-red-700 ring-1 ring-red-200' },
    blue: { icon: 'bg-sky-50 text-sky-700 ring-1 ring-sky-200' },
    purple: { icon: 'bg-purple-50 text-purple-700 ring-1 ring-purple-200' },
  };
  return styles[tone] || styles.blue;
}

function formatUnit(apartment, tower) {
  const parts = [apartment, tower].filter((value) => value && value !== '-');
  return parts.length ? parts.join(' · ') : '-';
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
    timeZone: SAO_PAULO_TIMEZONE,
  }).format(new Date(value));
}

function areStatsEqual(left, right) {
  return left.entriesToday === right.entriesToday
    && left.deniedToday === right.deniedToday
    && left.visitorsToday === right.visitorsToday
    && left.onlineDevices === right.onlineDevices;
}
